import React, { memo } from 'react';
import { connect } from 'react-redux';
import { hot } from 'react-hot-loader';
import Link from 'redux-first-router-link';
import BreadcrumbsSD from './BreadcrumbsSD';
import { resetFilter } from '../../actions/environment';
import { translate, prefix, removeLanguagePrefix } from '../../utils/locale';
import { titleCase, sentenceCase } from '../../utils/string';
import { useLanguageURLRoots } from '../../config';
import s from './Breadcrumbs.scss';

const areEqual = (prevProps, { pageStatus, pageData, productCache, setup, location }) => {
	if (pageStatus.loading) return true;
	if (!productCache[setup.market] || !productCache[setup.market][setup.pricelist]) return true;
	const currentPath = location.pathname.replace(/^\//, '').replace(/\/$/, '');
	if (location.type !== 'SELECTION' && !pageData[currentPath]) return true;

	return false;
};

const Breadcrumbs = ({ pageData, location, filter, product = '', productCache, setup, ...props }) => {
	const filtering = productCache[setup.market] && productCache[setup.market][setup.pricelist];
	let currentPath = location.pathname.indexOf('#') !== -1 ? location.pathname.slice(0, location.pathname.indexOf('#')) : location.pathname;
	currentPath = Object.keys(setup.languages).length > 1 && !useLanguageURLRoots ? removeLanguagePrefix(currentPath) : currentPath.replace(/^\//, '');
	const data = pageData[currentPath];

	if (!filtering || !data) return null;

	const pathSections = product ? product.replace(/^\//, '').replace(/\/$/, '').split('/') : currentPath.split('/');
	const isLocalized = filtering.localized[setup.language];
	const categoryTitles = isLocalized ? Object.keys(filtering.filterArrays.categories).reduce((acc, cat) => {
		const categoryKey = isLocalized.categories[cat] ? isLocalized.categories[cat].key : cat;
		const categoryDescription = isLocalized.categories[cat] ? isLocalized.categories[cat].description : filtering.filterDescriptions.categories[cat];
		return ({ ...acc, [categoryKey]: categoryDescription });
	}, {}) : filtering.filterDescriptions.categories;

	const resetFilterHandler = () => {
		props.resetFilter();
	};

	const links = [];

	if (!product) links.push({ uri: prefix(setup, '/'), name: translate(setup, 'breadcrumbsHome') });
	if (data?.baseType === 'categories') {
		pathSections.forEach((uri, i) => {
			const breadcrumbPath = pathSections.reduce((acc, path, j) => (i >= j ? `${acc}/${path}` : acc), '').slice(1);
			let title = '';

			if (data.baseType === 'brands' && translate(setup, 'designersUri', 'listings').text === uri) {
				title = translate(setup, 'designersTitle', 'listings');
			} else if ((i === pathSections.length - 1 || i === 0) && translate(setup, 'filterNewUri') === uri) {
				title = translate(setup, 'filterNew');
			} else if ((i === pathSections.length - 1 || i === 0) && translate(setup, 'filterSaleUri') === uri) {
				title = translate(setup, 'filterSale');
			} else if (data.baseType === 'brands' && i === 1) {
				title = filtering.filterDescriptions.brands[uri] ? filtering.filterDescriptions.brands[uri] : titleCase(uri);
			} else if (data.baseType === 'brands' && i > 1) {
				title = categoryTitles[breadcrumbPath.split('/').slice(2).join('/')];
			} else if (categoryTitles[breadcrumbPath]) {
				title = categoryTitles[breadcrumbPath];
			}

			if (!title) return;

			links.push({ uri: prefix(setup, breadcrumbPath), name: title, onClick: resetFilterHandler });
		});
	}
	if (data?.baseType !== 'categories' && pathSections.length > 1) {
		pathSections.forEach((page, i) => {
			const breadcrumbPath = pathSections.reduce((acc, path, j) => (i >= j ? `${acc}/${path}` : acc), '').slice(1);

			links.push({ uri: prefix(setup, breadcrumbPath), name: sentenceCase(page) });
		});
	}
	if (data?.content?.meta?.default) {
		links.push({ uri: prefix(setup, data.content.meta.uri), name: data.content.meta.default });
	}
	if (location.type === 'SELECTION') {
		links.push({ uri: prefix(setup, 'selection'), name: translate(setup, 'bag') });
	}

	return (
		<div className={[s.breadcrumbs, product ? s.breadcrumbs__productPage : null].join(' ')}>
			<BreadcrumbsSD list={links} />
			<div className={s.breadcrumbs_inner}>
				<div className={s.breadcrumbs_inner_links}>
					{ links.map(({ name, uri, ...extraAttrs }) => <Link key={uri} className={s.breadcrumbs_inner_link} to={uri} {...extraAttrs}><span>{ name }</span></Link>) }
				</div>
			</div>
		</div>
	);
};

const mapState = ({ pageStatus, pageData, location, environment, productCache, filter }, props) => ({
	pageStatus,
	pageData,
	location,
	setup: environment.setup,
	productCache,
	filter,
	...props,
});

export default hot(module)(connect(mapState, { resetFilter })(memo(Breadcrumbs, areEqual)));
